/**
 * Historical Emporium - Recently Viewed Products
 * Tracks and displays recently viewed products with fallback support
 * 
 * Features:
 * - LocalStorage tracking
 * - AJAX product fetching
 * - Graceful fallback to manual products
 * - Privacy-aware (respects browser settings)
 */

class RecentlyViewed {
  constructor() {
    this.storageKey = 'he_recently_viewed';
    this.maxItems = 12;
    this.section = document.querySelector('.recently-viewed-section');
    this.grid = document.querySelector('.recently-viewed-grid');
    this.init();
  }
  
  init() {
    // Check if tracking is enabled
    const trackingEnabled = this.section?.dataset.enableTracking === 'true';
    
    if (!trackingEnabled) {
      return; // Use fallback products only
    }
    
    // Track current product (if on product page)
    if (this.isProductPage()) {
      this.trackCurrentProduct();
    }
    
    // Load recently viewed (if section exists)
    if (this.section && this.grid) {
      this.loadRecentlyViewed();
    }
  }
  
  /**
   * Check if we're on a product page
   */
  isProductPage() {
    return window.location.pathname.includes('/products/') && 
           !window.location.pathname.includes('/products.json');
  }
  
  /**
   * Get product handle from URL
   */
  getCurrentProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?]+)/);
    return match ? match[1] : null;
  }
  
  /**
   * Track current product
   */
  trackCurrentProduct() {
    const handle = this.getCurrentProductHandle();
    if (!handle) return;
    
    try {
      // Get current list
      let viewed = this.getViewed();
      
      // Remove if already exists (to move to front)
      viewed = viewed.filter(h => h !== handle);
      
      // Add to front (most recent first)
      viewed.unshift(handle);
      
      // Limit to maxItems
      if (viewed.length > this.maxItems) {
        viewed = viewed.slice(0, this.maxItems);
      }
      
      // Save to localStorage
      localStorage.setItem(this.storageKey, JSON.stringify(viewed));
    } catch (e) {
      console.warn('Recently Viewed: Unable to save to localStorage', e);
    }
  }
  
  /**
   * Get viewed products from localStorage
   */
  getViewed() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.warn('Recently Viewed: Unable to read from localStorage', e);
      return [];
    }
  }
  
  /**
   * Load and render recently viewed products
   */
  async loadRecentlyViewed() {
    const handles = this.getViewed();
    
    // Exclude current product if on product page
    const currentHandle = this.getCurrentProductHandle();
    const filteredHandles = currentHandle 
      ? handles.filter(h => h !== currentHandle)
      : handles;
    
    if (filteredHandles.length === 0) {
      // No history: keep fallback products (already rendered by Liquid)
      // If no fallback products either, hide the section
      this.checkAndHideIfEmpty();
      return;
    }
    
    // Get max products from section settings
    const maxProducts = parseInt(this.section.dataset.maxProducts) || 6;
    const handlesToFetch = filteredHandles.slice(0, maxProducts);
    
    // Fetch products
    const products = await this.fetchProducts(handlesToFetch);
    
    if (products.length > 0) {
      this.renderProducts(products);
    } else {
      // Failed to fetch products, keep fallback or hide if empty
      this.checkAndHideIfEmpty();
    }
  }
  
  /**
   * Check if section is empty and hide it
   */
  checkAndHideIfEmpty() {
    if (!this.grid) return;
    
    // Count actual product items (not empty state messages)
    const productItems = this.grid.querySelectorAll('.recently-viewed-item');
    
    if (productItems.length === 0) {
      // No products at all, hide the entire section
      if (this.section) {
        this.section.style.display = 'none';
      }
    }
  }
  
  /**
   * Fetch products from Shopify API
   */
  async fetchProducts(handles) {
    const fetchPromises = handles.map(handle => 
      fetch(`/products/${handle}.js`)
        .then(res => {
          if (!res.ok) throw new Error('Product not found');
          return res.json();
        })
        .catch(err => {
          console.warn(`Recently Viewed: Unable to fetch product ${handle}`, err);
          return null;
        })
    );
    
    const results = await Promise.all(fetchPromises);
    
    // Filter out failed requests and unavailable products
    return results.filter(product => product !== null);
  }
  
  /**
   * Render products in grid
   */
  renderProducts(products) {
    if (!this.grid) return;
    
    // Clear existing fallback products
    this.grid.innerHTML = '';
    
    // Render fetched products
    products.forEach(product => {
      const card = this.createProductCard(product);
      this.grid.appendChild(card);
    });
  }
  
  /**
   * Create product card element
   */
  createProductCard(product) {
    // Create card container
    const item = document.createElement('a');
    item.href = product.url;
    item.className = 'recently-viewed-item';
    
    // Create image container
    const imageDiv = document.createElement('div');
    imageDiv.className = 'recently-viewed-image';
    
    // Create image
    const img = document.createElement('img');
    img.src = this.getImageUrl(product.featured_image, 300);
    img.alt = product.featured_image_alt || product.title;
    img.loading = 'lazy';
    img.width = 300;
    img.height = 300;
    
    // Assemble card
    imageDiv.appendChild(img);
    item.appendChild(imageDiv);
    
    return item;
  }
  
  /**
   * Get optimized image URL
   */
  getImageUrl(imageUrl, width) {
    if (!imageUrl) return '';
    
    // Shopify CDN image optimization
    const url = imageUrl.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${width}x.$1`);
    return url;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new RecentlyViewed();
  });
} else {
  new RecentlyViewed();
}

